import React, {
  useEffect, useRef, useState,
} from 'react';
import useThemeStore from '$/store/useThemeStore';
import { useLanguageStore } from '$/store/languageStore';
import RoboneoModal from '$/components/Modal';
import { useCommonStore } from '@/store/useCommonStore';
import style from './index.module.scss';

interface FeedbackModalProps {
  userInfo: { uid: string; screenName: string; avatar: string; } | undefined;
  env: 'pre' | 'beta' | 'release';
  room_id?: string;
  task_id?: string;
  activeTab?: 'history' | '';
  image?: string;
  video?: {
    url: string;
    cover: string;
  };
  client_id: string;
  gid: string;
  channel: string;
  language: string;
  onClose: () => void;
  open: boolean;
  getAccessToken: () => string;
}

// 客服反馈需要盖过 Team Studio 工作区 5000 和媒体预览 5900。
const FEEDBACK_MODAL_Z_INDEX = 6300;

/**
 * 在 COEP（如 team_studio）页面嵌入跨域反馈 iframe 前设置 credentialless，
 * 否则浏览器会拦截加载；需在赋值 src 之前设置。
 *
 * @param iframe 反馈 iframe 元素
 * @param src 反馈页地址
 */
function applyCredentiallessIframeSrc(iframe: HTMLIFrameElement | null, src: string) {
  if (!iframe || !src) return;
  const el = iframe as HTMLIFrameElement & { credentialless?: boolean; };
  const needReload = !el.credentialless || iframe.getAttribute('src') !== src;
  // 使用 Reflect，避免直接改写参数属性触发 eslint；credentialless 需用 DOM 属性 API
  Reflect.set(iframe, 'credentialless', true);
  if (needReload) {
    // credentialless 必须在导航前生效；必要时强制重载
    Reflect.set(iframe, 'src', src);
  }
}

/**
 * 渲染客服反馈弹窗，并固定在媒体预览与脚本工作区之上。
 */
function FeedbackModal(props: FeedbackModalProps) {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    client_id,
    env,
    gid,
    channel,
    language,
    room_id,
    task_id,
    image,
    video,
    onClose,
    open,
    getAccessToken,
    userInfo,
    activeTab,
  } = props;
  const languageStore = useLanguageStore((state) => state.languageStore);
  const getLatestRoomId = useCommonStore((state) => state.getLatestRoomId);
  const theme = useThemeStore((state) => state.theme);
  const [origin, setOrigin] = useState('');
  // 用 | null 得到 MutableRefObject，便于 callback ref 回写
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const originRef = useRef(origin);
  originRef.current = origin;

  const preFix = env === 'release' ? '' : `${env}-`;
  let iframeSrc = `https://${preFix}feedback.meitu.com/feedback/submit?client_id=${client_id}&theme=${theme}`;
  if (activeTab) {
    iframeSrc = `${iframeSrc}&active-tab=${activeTab}`;
  }
  /**
   * 组装反馈透传数据（房间 / 任务 / 媒体）。
   *
   * @param KnownScope feedback-web-bridge 作用域枚举
   */
  async function getExtraData(KnownScope: any) {
    let ensureRoomId = room_id;
    if (!ensureRoomId) {
      ensureRoomId = await getLatestRoomId();
    }
    return {
      scope: KnownScope.FeedbackSubmit,
      body: {
        images: image ? [image] : [],
        video,
        ext: {
          room_id: ensureRoomId,
          task_id,
        },
      },
    };
  }

  useEffect(() => {
    const init = async () => {
      const {
        KnownScope,
        bindHandlers,
        businessMessenger,
        setMessengerOrigin,
      } = await import('@meitu/feedback-web-bridge/es/business');

      businessMessenger.setTarget(iframeSrc);
      bindHandlers({
        getAppInfo() {
          return new Promise((resolve) => {
            resolve({
              gid,
              language,
              channel,
              theme,
            });
          });
        },
        // 跨域时必须提供该方法
        getMeituAccountEncryptedToken() {
          return {
            encryptedToken: getAccessToken() || '',
          };
        },
        getUserInfo() {
          return new Promise((resolve) => {
            resolve({
              /* 大账号UID */
              uid: userInfo?.uid,
              /* 用户昵称 */
              screenName: userInfo?.screenName,
              /* 头像地址 */
              avatar: userInfo?.avatar,
            });
          });
        },
        // agent中反馈需要透传数据
        sendExtraData() {
          return getExtraData(KnownScope);
        },
      });
      setOrigin(setMessengerOrigin(iframeSrc));
    };
    init();
    // 与历史实现一致：仅在 open 变化时重绑 messenger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !origin) return;
    applyCredentiallessIframeSrc(iframeRef.current, origin);
  }, [open, origin]);

  /**
   * Modal destroyOnClose 后 iframe 重新挂载时立刻写入 credentialless + src，
   * 避免 useEffect 时序下 ref 仍为空导致白屏。
   *
   * @param el iframe 元素
   */
  function handleIframeRef(el: HTMLIFrameElement | null) {
    iframeRef.current = el;
    if (el && originRef.current) {
      applyCredentiallessIframeSrc(el, originRef.current);
    }
  }

  return (
    <RoboneoModal
      open={open}
      wrapClassName={style.feedbackModal}
      bodyClassName={style.feedbackModalBody}
      onCancel={onClose}
      closable
      destroyOnClose
      zIndex={FEEDBACK_MODAL_Z_INDEX}
    >
      <div className={style.title}>{languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_user_feedback)}</div>
      {/* origin 就绪后写入 src；callback 中补 credentialless 并按需重载，兼容 COEP 页面 */}
      <iframe ref={handleIframeRef} title="feedback" src={origin || undefined} />
    </RoboneoModal>

  );
}
export default FeedbackModal;
